import { AxiosError } from 'axios';
import { DialogHTMLAttributes, forwardRef, ForwardRefRenderFunction, Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollbar } from 'src/common';
import { Button, Dialog, Divider } from 'src/common/components';
import Column from 'src/common/components/column';
import Row from 'src/common/components/row';
import { toast } from 'src/common/components/toast';
import { User } from 'src/modules/auth/contexts';

import { useUsers } from '../../hooks/use-users';
import { userService } from '../../services/user.service';
import { CancelButton, DialogContent } from './styles';

interface DeleteDialogProps extends DialogHTMLAttributes<HTMLDialogElement> {
    user: User | null;
}

const DeleteDialogBase: ForwardRefRenderFunction<HTMLDialogElement, DeleteDialogProps> = ({ user, ...props }, ref) => {
    const { showScrollBar } = useScrollbar();
    const { t } = useTranslation();
    const { handleRequestList } = useUsers();

    const [isLoading, setIsLoading] = useState(false);

    const handleClose = useCallback(() => {
        document.querySelector<HTMLDialogElement>(`dialog#delete-user-dialog`)?.close();
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            await userService.delete(user.id);
            handleRequestList({
                currentPage: 1,
            });
            showScrollBar();
            toast.success(t('general.success'), '');
            handleClose();
        } catch (e) {
            if (e instanceof AxiosError) {
                if (e.response?.data.message === 'general.exclude_yourself') {
                    toast.error(t('general.error'), t('general.exclude_yourself'));
                    return;
                }
            }
            toast.error(t('general.error'), '');
        } finally {
            setIsLoading(false);
        }
    }, [handleClose, handleRequestList, showScrollBar, t, user]);

    const data = useMemo(() => {
        if (!user) return <Fragment />;

        return (
            <Fragment>
                <Row justify="center" width="100%" align="start">
                    <h4>{t('configurations.user_delete')}</h4>
                </Row>
                <Column align="center" gap={16} flex width="100%">
                    <Column align="center">
                        <p>
                            {t('configurations.delete_the_user')} <b>{JSON.stringify(user.name || user.email)}</b>?
                        </p>
                        <p>{t('configurations.this_action')}</p>
                    </Column>
                    <Divider />
                </Column>
                <Row align="flex-end" gap={16} flex width="100%">
                    <CancelButton onClick={handleClose}>{t('general.cancel')}</CancelButton>
                    <Button isLoading={isLoading} type="reset" color="alert" onClick={handleConfirm}>
                        {t('configurations.delete')}
                    </Button>
                </Row>
            </Fragment>
        );
    }, [handleClose, handleConfirm, isLoading, t, user]);

    return (
        <Dialog ref={ref} id="delete-user-dialog" {...props}>
            <DialogContent>{data}</DialogContent>
        </Dialog>
    );
};

const DeleteDialog = forwardRef(DeleteDialogBase);

export default DeleteDialog;
